import {
  normalizeSpeciesInputKey,
  SPECIES_KEY_ALIASES,
} from "src/utils/speciesCatalog";

const toNumberSafe = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const escapeRegExp = (value) =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const resolveMoneySeparators = (locale) => {
  const requestedLocale =
    typeof locale === "string" ? locale.trim() : "";
  if (!requestedLocale) return null;

  try {
    if (Intl.NumberFormat.supportedLocalesOf([requestedLocale]).length !== 1) {
      return null;
    }
    const parts = new Intl.NumberFormat(requestedLocale).formatToParts(
      1234567.89
    );
    const decimal = parts.find((part) => part.type === "decimal")?.value;
    const group = parts.find((part) => part.type === "group")?.value;
    if (!decimal || !group || decimal === group) return null;
    return {
      decimal: decimal.replace(/[\u00a0\u202f]/g, " "),
      group: group.replace(/[\u00a0\u202f]/g, " "),
    };
  } catch (_error) {
    return null;
  }
};

const parseLocalizedMoneyInput = (value, locale) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  let input = String(value)
    .trim()
    .replace(/[\u00a0\u202f]/g, " ")
    .replace(/^R\$\s*/i, "");
  if (!input) return null;

  const separators = resolveMoneySeparators(locale);
  if (!separators) {
    if (!/^\+?\d+$/.test(input)) return null;
    const parsedInteger = Number(input);
    return Number.isFinite(parsedInteger) ? parsedInteger : null;
  }

  if (input.startsWith("+")) {
    input = input.slice(1);
  }
  if (!input) return null;

  if (separators.group === " ") {
    input = input.replace(/\s+/g, " ");
  } else if (/\s/.test(input)) {
    return null;
  }

  const decimalParts = input.split(separators.decimal);
  if (decimalParts.length > 2) return null;
  const [integerPart, fractionPart] = decimalParts;
  if (
    !integerPart ||
    (fractionPart !== undefined && !/^\d+$/.test(fractionPart))
  ) {
    return null;
  }

  const groupPattern = new RegExp(escapeRegExp(separators.group), "g");
  let normalizedInteger;
  if (integerPart.includes(separators.group)) {
    const groups = integerPart.split(separators.group);
    if (
      !/^\d{1,3}$/.test(groups[0]) ||
      groups.slice(1).some((group) => !/^\d{3}$/.test(group))
    ) {
      return null;
    }
    normalizedInteger = integerPart.replace(groupPattern, "");
  } else {
    if (!/^\d+$/.test(integerPart)) return null;
    normalizedInteger = integerPart;
  }

  const normalized =
    fractionPart === undefined
      ? normalizedInteger
      : `${normalizedInteger}.${fractionPart}`;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const COMPACT_NUMBER_UNITS = [
  { divisor: 1_000_000_000_000, suffix: "T" },
  { divisor: 1_000_000_000, suffix: "B" },
  { divisor: 1_000_000, suffix: "M" },
  { divisor: 1_000, suffix: "k" },
];

const compactNumberParts = (value) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return null;
  }
  const parsed = toNumberSafe(value);
  if (parsed === null) return null;
  const unit = COMPACT_NUMBER_UNITS.find(
    ({ divisor }) => Math.abs(parsed) > divisor
  );
  return unit
    ? { value: parsed / unit.divisor, suffix: unit.suffix }
    : { value: parsed, suffix: "" };
};

const formatNumber = (
  value,
  {
    locale = "en-US",
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    fallback = "—",
  } = {}
) => {
  const parsed = toNumberSafe(value);
  if (parsed === null) return fallback;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(parsed);
};

const formatCurrency = (
  value,
  {
    locale = "en-US",
    currency = "USD",
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    fallback = "—",
    currencySymbol = null,
    symbolPlacement = "suffix",
    symbolSeparator = " ",
  } = {}
) => {
  const parsed = toNumberSafe(value);
  if (parsed === null) return fallback;
  if (currencySymbol) {
    const base = formatNumber(parsed, {
      locale,
      minimumFractionDigits,
      maximumFractionDigits,
      fallback,
    });
    if (base === fallback) return fallback;
    return symbolPlacement === "prefix"
      ? `${currencySymbol}${symbolSeparator}${base}`
      : `${base}${symbolSeparator}${currencySymbol}`;
  }
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(parsed);
};

const formatUsdc = (
  value,
  {
    includeCode = true,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    fallback = "—",
  } = {}
) => {
  const formatted = formatCurrency(value, {
    locale: "en-US",
    currency: "USD",
    minimumFractionDigits,
    maximumFractionDigits,
    fallback,
  });
  if (formatted === fallback) return fallback;
  return includeCode ? `${formatted} USDC` : formatted;
};

const formatPercent = (
  value,
  {
    locale = "en-US",
    decimals = 2,
    fallback = "—",
    suffix = " %",
  } = {}
) => {
  const parsed = toNumberSafe(value);
  if (parsed === null) return fallback;
  const base = formatNumber(parsed, {
    locale,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    fallback,
  });
  if (base === fallback) return fallback;
  return `${base}${suffix}`;
};

const normalizeSpeciesKey = (value) => {
  const inputKey = normalizeSpeciesInputKey(value);
  return SPECIES_KEY_ALIASES[inputKey] || inputKey;
};

const normalizeSpeciesList = (species) =>
  Array.isArray(species)
    ? species.map((s) => normalizeSpeciesKey(s)).filter((s) => s)
    : [];

const resolveBySpeciesEntry = (bySpecies, species) => {
  if (!bySpecies || typeof bySpecies !== "object") {
    return undefined;
  }
  const rawKey = typeof species === "string" ? species.trim() : species;
  if (rawKey && Object.prototype.hasOwnProperty.call(bySpecies, rawKey)) {
    return bySpecies[rawKey];
  }
  const canonicalKey = normalizeSpeciesKey(species);
  if (
    canonicalKey &&
    Object.prototype.hasOwnProperty.call(bySpecies, canonicalKey)
  ) {
    return bySpecies[canonicalKey];
  }
  return undefined;
};

// Detect species that collapse to the SAME canonical key — i.e. the same crop
// entered under two locale names (e.g. `Cacau` + `Cocoa`, `Açaí` + `acai`).
// This is forbidden: the same species must never exist under two language keys.
// Returns the list of canonical keys that appear more than once (deduped, in
// first-seen order). Blank rows are ignored.
const findDuplicateCanonicalSpecies = (species) => {
  if (!Array.isArray(species)) return [];
  const seen = new Set();
  const duplicates = [];
  species.forEach((raw) => {
    const key = normalizeSpeciesKey(raw);
    if (!key) return;
    if (seen.has(key)) {
      if (!duplicates.includes(key)) duplicates.push(key);
    } else {
      seen.add(key);
    }
  });
  return duplicates;
};

const hasNonEmpty = (value) =>
  value !== null && value !== undefined && String(value).trim().length > 0;

// Merge two per-year production entries ({ total_supply, final_price }),
// preferring whichever side already has a meaningful (non-empty) value.
// `target` wins ties so an explicitly-entered value is never clobbered by
// a later prediction/legacy default.
const mergeProductionYearEntry = (target, source) => {
  const targetEntry = target && typeof target === "object" ? target : {};
  const sourceEntry = source && typeof source === "object" ? source : {};
  return {
    total_supply: hasNonEmpty(targetEntry.total_supply)
      ? String(targetEntry.total_supply)
      : hasNonEmpty(sourceEntry.total_supply)
      ? String(sourceEntry.total_supply)
      : "",
    final_price: hasNonEmpty(targetEntry.final_price)
      ? String(targetEntry.final_price)
      : hasNonEmpty(sourceEntry.final_price)
      ? String(sourceEntry.final_price)
      : "",
  };
};

// Re-key a production `bySpecies` map by the canonical species key so that
// locale duplicates (e.g. `cacau` + `cocoa`, `açaí` + `acai`) collapse into a
// single canonical entry. Non-empty `total_supply`/`final_price` values are
// preserved when duplicate keys are merged. Pure function — returns a new
// object, does not mutate the input.
const mergeBySpeciesToCanonical = (bySpecies) => {
  if (!bySpecies || typeof bySpecies !== "object") {
    return {};
  }
  const merged = {};
  Object.entries(bySpecies).forEach(([rawKey, years]) => {
    const canonicalKey = normalizeSpeciesKey(rawKey);
    if (!canonicalKey) return;
    if (!merged[canonicalKey]) merged[canonicalKey] = {};
    const targetYears = merged[canonicalKey];
    Object.entries(years && typeof years === "object" ? years : {}).forEach(
      ([yearKey, entry]) => {
        targetYears[yearKey] = mergeProductionYearEntry(
          targetYears[yearKey],
          entry
        );
      }
    );
  });
  return merged;
};

const safeLocaleCase = (value, method, locale) => {
  try {
    return String(value)[method](locale || "en");
  } catch (_error) {
    return method === "toLocaleUpperCase"
      ? String(value).toUpperCase()
      : String(value).toLowerCase();
  }
};

const titleCaseSpeciesWord = (word, locale) => {
  const lower = safeLocaleCase(word, "toLocaleLowerCase", locale);
  const chars = Array.from(lower);
  if (!chars.length) return "";
  return `${safeLocaleCase(chars[0], "toLocaleUpperCase", locale)}${chars
    .slice(1)
    .join("")}`;
};

const sanitizeSpeciesDisplayName = (value, locale = "en") => {
  const cleaned = String(value || "")
    .normalize("NFC")
    .replace(/[\u0000-\u001F\u007F]/g, " ")
    .replace(/[^\p{L}\p{M}\p{N}\s.'’/-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return "";

  return cleaned.replace(/[\p{L}\p{M}\p{N}]+/gu, (word) =>
    titleCaseSpeciesWord(word, locale)
  );
};

export {
  compactNumberParts,
  findDuplicateCanonicalSpecies,
  formatCurrency,
  formatNumber,
  formatPercent,
  formatUsdc,
  mergeBySpeciesToCanonical,
  mergeProductionYearEntry,
  normalizeSpeciesInputKey,
  normalizeSpeciesKey,
  normalizeSpeciesList,
  parseLocalizedMoneyInput,
  resolveBySpeciesEntry,
  sanitizeSpeciesDisplayName,
};
